import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Alert, Button, Input, Select, Space } from 'antd'
import { useStore } from 'effector-react'

import { useCommentForm } from './hooks'
import { comment, submitForm } from './model'
import { $userRole } from '../../../features/user'

type Props = {
  type: string
  entityId: string | number
}

type FormData = {
  message: string
  visibility: string
  type: number
}

const defaultValues: FormData = {
  message: '',
  visibility: 'all',
  type: 2,
}

// Ant Design version of CommentForm
export const CommentFormAntd = ({ type, entityId }: Props) => {
  const { form: formState } = useCommentForm()
  const { loading, error } = formState
  const userRole = useStore($userRole)

  const { control, handleSubmit, reset } = useForm<FormData>({
    mode: 'onChange',
    defaultValues,
  })

  useEffect(() => {
    const unsub = comment.done.watch(() => reset(defaultValues))
    return unsub
  }, [reset])

  const onSubmit = (data: FormData) => {
    submitForm({
      entityType: type,
      entityId: String(entityId),
      message: data.message,
      visibility: data.visibility,
      department: userRole?.[0] ?? '',
      type: Number(data.type),
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="message"
        control={control}
        rules={{ required: 'Field is required' }}
        render={({ field, fieldState: { error: fieldError } }) => (
          <div style={{ marginBottom: 12 }}>
            <Input.TextArea
              {...field}
              placeholder="Comment"
              autoSize={{ minRows: 3 }}
              status={fieldError ? 'error' : undefined}
            />
            {fieldError && (
              <div
                className="ant-form-item-explain-error"
                style={{ marginTop: 4 }}
              >
                {fieldError.message}
              </div>
            )}
          </div>
        )}
      />

      <Space.Compact block style={{ marginBottom: 12 }}>
        <Controller
          name="visibility"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              style={{ width: '50%' }}
              options={[
                { value: 'all', label: 'All' },
                { value: 'support', label: 'Support' },
                { value: 'finance', label: 'Finance' },
                { value: 'dealing', label: 'Dealing' },
              ]}
            />
          )}
        />
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              style={{ width: '50%' }}
              options={[
                { value: 2, label: 'Info' },
                { value: 1, label: 'Warning' },
                { value: 0, label: 'Alert' },
              ]}
            />
          )}
        />
      </Space.Compact>

      <Button type="primary" htmlType="submit" loading={loading} block>
        Comment
      </Button>

      {error && (
        <Alert
          type="error"
          showIcon
          message={error.msg}
          style={{ marginTop: 12 }}
        />
      )}
    </form>
  )
}
